// The new valley as a scene (`?scene=world`, phase C, gates C1 and C2): the valley's machinery (the Wanderer on
// @rpg/controller, the camera, the low-poly look; no stub mount, decision 0084: it stays on the A2.3 course) and the
// joined fight, on @rpg/world's valley from the
// layout instead of the test course. Nothing of the world is in the scene at the start: the content build primes the
// sim tier (src/world/sim.ts: the chunks round the hero and the camps, their colliders and nav tiles), and every
// step after streams them. The phase-B encounter is moved onto the layout: its waves come from the "valley" row and
// its spawn points from the layout's camp placements (camps beside the road, the Hollow Warden at the ruins).
// `?encounter=off` boots without it; `?seed=N` builds the valley from another seed.
// Phase C joined: the layout's buildings and props come from the places stream's kit (places.ts: their colliders in
// their chunk's body), every house's room is an interior for the interiors director (the "places" module), the brook
// and the pond are water shapes fitted to the carved ground, and the sky stream's clock and weather run in the same
// kernel game (the "sky" module). The things stream's gatherables, breakables and signs are the layout's placements of
// those kinds (the "things" module), their bodies loading and unloading with their chunk. The navmesh's tiles (2 × 2 a
// chunk) are built in slices, a unit of work a step (src/world/sim.ts). This is the spike's default scene since phase C.
import type { WaterVolume } from "@rpg/collision";
import { NavTileSlicer, buildNavWorld, navGeometry, type NavWorld } from "@rpg/nav";
import type { Runtime } from "@rpg/runtime";
import { createWorld, type World } from "@rpg/world";
import type { SpikeContent } from "../content";
import { FIGHT_LAYERS, FIGHT_SOLVER_ITERATIONS, createFight, readFightContent, type Fight, type FightEncounter } from "../enemies/fight";
import { VALLEY_LAYERS } from "../valley/course";
import { VALLEY_NAV } from "../valley/fight";
import type { ValleyRig, ValleyVariant } from "../valley/scene";
import type { GameModule, World as KernelWorld } from "@rpg/kernel";
import type { GameEvents } from "../core";
import type { SceneName } from "../sim/host";
import { createInteriorsModule, type PlacesSim } from "../places/module";
import { createSky, readSkyContent, type Sky } from "../sky/module";
import { readWorldContent, type WorldContent } from "./content";
import { createWorldKit, kitOwners, worldRooms, worldWater, type WorldKit, type WorldRoom } from "./places";
import { chunkNavSource, createWorldSim, type WorldSim } from "./sim";
import { readThingsContent, type Placement as ThingPlacement, type ThingsContent } from "../things/content";
import { Conditions } from "@rpg/story";
import { RPG_FAULTS, readRpgContent, type MarketPlace, type RpgContent } from "../rpg/content";
import { createRpg, type Rpg } from "../rpg/module";
import { BREAKABLE_IDS, THINGS_LAYERS, createThings, dynamicNavShapes, type OtherInteractables, type Things } from "../things/module";
import type { RngStreams } from "@rpg/runtime";

/** A chunk is NAV_SPLIT × NAV_SPLIT nav tiles. */
export const NAV_SPLIT = 2;

/** The encounter row the world's camps take their waves from. */
export const WORLD_ENCOUNTER = "valley";

/** What an addon to the world scene gets (phase D: the story layer and the other RPG streams' modules join the valley's
 *  one kernel game through these, not through edits to this file). */
export interface WorldAddonParts {
  world: World;
  rig: ValleyRig;
  rng: RngStreams;
  fight: () => Fight;
  things: () => Things;
  thingsContent: ThingsContent;
  sky: () => Sky;
  nav: () => NavWorld;
}

/** An addon's share of the world scene: its modules (after the world's), its build, hashed components, interactables in
 *  the things' registry, and spots whose chunks the sim tier keeps loaded. */
export interface WorldAddonMount {
  modules: readonly GameModule<GameEvents>[];
  build?(w: KernelWorld): void;
  hashed?: readonly string[];
  interactables?: OtherInteractables;
  pins?(): { x: number; z: number }[];
}

/** An addon to the world scene. */
export type WorldAddon = (parts: WorldAddonParts) => WorldAddonMount;

/** The world scene's parts for the sim mount. */
export interface WorldMount {
  variant: ValleyVariant;
  content: WorldContent;
  world: World;
  /** The kit over the layout's pieces (seam 2) and every kit placement's owner number for the cut-away. */
  kit: WorldKit;
  owners: Map<string, number>;
  /** The houses' rooms (seamless interiors) and the brook and the pond as water shapes (seam 3). */
  rooms: WorldRoom[];
  water: WaterVolume[];
  /** Once the scene is set up. */
  fight(): Fight;
  /** The clock and the weather (seam 4) and the interiors director. */
  sky(): Sky;
  places(): PlacesSim;
  /** The things (seam 5): the layout's gatherables, breakables and signs. */
  thingsContent: ThingsContent;
  things(): Things;
  sim(): WorldSim;
  nav(): NavWorld;
  rig(): ValleyRig;
  /** Phase D joined: the rpg layer (progression, the inventory, gear, gold, shops, the forge and the pot) on the
   *  valley's fight and things, its content, the conditions it prices with, and its stations from the layout. */
  rpgContent: RpgContent;
  conditions: Conditions;
  stations: readonly MarketPlace[];
  rpg(): Rpg;
}

/** The rpg layer's stations in the valley: the layout's `station` placements, each taking its verb and reach from the
 *  market table's row for the same station (forge, pot). */
export function layoutStations(world: World, rc: RpgContent): MarketPlace[] {
  return world.placements("station").map((p) => {
    const row = rc.market.find((m) => m.kind === "station" && m.station === p.piece);
    if (!row) throw new Error(`world: station "${p.id}" names "${p.piece}", which no market row describes`);
    return { ...row, id: p.id, pos: [p.pos[0], p.pos[1]], yaw: p.yaw ?? 0 };
  });
}

/** A later phase's parts on the world scene (phase D2's discovery module, in the hud lab): its modules join the one
 *  kernel game after the world's, its build runs last and its component types are hashed. `mount` is the scene's
 *  mount (valid once the scene returns). */
export type WorldExtra = (o: { rig: ValleyRig; mount: () => WorldMount }) => { modules: GameModule<GameEvents>[]; build(w: KernelWorld): void; hashed: readonly string[] };

/** The phase-B encounter on the layout: the waves of `WORLD_ENCOUNTER`, the spawn points at the layout's camps. */
export function layoutEncounter(base: FightEncounter, world: World): FightEncounter {
  const camps = world.placements("camp");
  const spawns = base.spawns.map((s) => {
    const p = camps.find((c) => c.id === s.id);
    if (!p) throw new Error(`world: the encounter's spawn "${s.id}" has no camp placement in the layout`);
    const spread = typeof p.params?.spread === "number" ? p.params.spread : s.spread;
    return { id: s.id, x: p.pos[0], z: p.pos[1], spread, facing: p.yaw };
  });
  return { ...base, id: "world", spawns };
}

/** The world scene. `seed`: the world's (default the layout's). */
export function worldScene(o: { content: SpikeContent; runtime: Runtime; difficulty?: string; encounter?: boolean; seed?: number; name?: SceneName; extra?: WorldExtra; addons?: readonly WorldAddon[]; conditions?: Conditions }): WorldMount {
  const wc = readWorldContent(o.content);
  const kit = createWorldKit(o.content);
  // the floors and decks answer the footsteps (improvement step 3, IMP-0026): the kit gives the footprint, the layout the tag
  const world = createWorld({ layout: wc.layout, rules: wc.rules, ...(o.seed !== undefined ? { seed: o.seed } : {}), colliders: (p) => kit.colliders(p), solidKinds: ["building", "prop", "camp"], grounds: (p) => kit.grounds(p, wc.grounds) });
  const owners = kitOwners(world, kit);
  const rooms = worldRooms(world, kit, owners);
  const water = worldWater(world);
  const skyContent = readSkyContent(o.content);
  // the things are the layout's placements of their kinds (seam 5)
  const thingKinds = new Set(["gatherable", "breakable", "interactable"]);
  const thingRows: ThingPlacement[] = world
    .placements()
    .filter((p) => thingKinds.has(p.kind))
    .map((p) => ({ id: p.id, kind: p.kind as ThingPlacement["kind"], piece: p.piece, pos: [p.pos[0], p.pos[1]], yaw: p.yaw, seed: p.seed }));
  const thingsContent = readThingsContent(o.content, thingRows);
  const chunkOfThing = new Map(world.placements().map((p) => [p.id, p.chunk]));
  const fc = readFightContent(o.content);
  const base = fc.encounters[WORLD_ENCOUNTER];
  if (!base) throw new Error(`world: no encounter "${WORLD_ENCOUNTER}"`);
  fc.encounters.world = layoutEncounter(base, world);
  const start = world.placements("spawn").find((p) => p.id === "start");
  if (!start) throw new Error("world: the layout needs the spawn placement \"start\"");
  const spawn = { x: start.pos[0], z: start.pos[1], yaw: start.yaw };
  const b = wc.layout.bounds;
  let fight: Fight | null = null;
  let sim: WorldSim | null = null;
  let nav: NavWorld | null = null;
  let rigRef: ValleyRig | null = null;
  let sky: Sky | null = null;
  let places: PlacesSim | null = null;
  let things: Things | null = null;
  const camps = world.placements("camp").map((p) => ({ x: p.pos[0], z: p.pos[1] }));
  // phase D joined: the rpg layer, made before the fight and the things (the fight asks it for the hero's move swaps
  // and weapons, the things put what they gather and collect into its inventory)
  const rpgContent = readRpgContent(o.content, RPG_FAULTS);
  if (rpgContent.issues.length) throw new Error(`world: the rpg content has problems: ${rpgContent.issues.join("; ")}`);
  // the one condition language (@rpg/story's): the story's when it joins (it registers its facts on the same one)
  const conditions = o.conditions ?? new Conditions();
  const stations = layoutStations(world, rpgContent);
  let rpg: Rpg | null = null;

  const variant: ValleyVariant = {
    name: o.name ?? "world",
    layers: { ...VALLEY_LAYERS, ...FIGHT_LAYERS, ...THINGS_LAYERS },
    // nothing built up front: the world streams in (src/world/sim.ts)
    course: () => ({ bodies: [], water, steps: [], ramps: [], stepLaneZ: 0 }),
    height: (x, z) => world.heightAt(x, z),
    spawn,
    // no stub mount in the world (decision 0084): it stays on the A2.3 course (`?scene=valley`)
    mountSpawn: null,
    solverIterations: FIGHT_SOLVER_ITERATIONS,
    extend({ rig, rng, actions }) {
      rigRef = rig;
      // a navmesh over the whole valley with no tiles: a chunk's tiles (2 × 2 of 16 m, so no unit of a tile's work is a
      // whole 32 m chunk's) are built from its geometry when it loads, a unit a step; the standing crates are dynamic
      // shapes, carved in while they stand
      const navSource = chunkNavSource(world, { margin: wc.streaming.navMargin, split: NAV_SPLIT, tileAt: () => nav!.tileAt });
      const n = buildNavWorld(
        navGeometry([]),
        { ...VALLEY_NAV, tileSize: Math.round(wc.layout.chunk / NAV_SPLIT / VALLEY_NAV.cellSize), bounds: [[b.x0, -40, b.z0], [b.x1, 80, b.z1]] },
        { tiles: "none", source: navSource.source, dynamic: dynamicNavShapes(thingsContent, (x, z) => world.heightAt(x, z)) },
      );
      nav = n;
      const chunkSlicer = new NavTileSlicer(n, { unitsPerSlice: thingsContent.unitsPerSlice });
      const r = createRpg({ content: rpgContent, rng, fight: () => f, things: () => t, conditions, groundAt: (x, z) => world.heightAt(x, z), places: stations });
      rpg = r;
      const f = createFight({
        collision: rig.collision,
        actions,
        rng,
        nav: n,
        content: fc,
        slowMo: (scale, s, ease) => o.runtime.clock.slowMo(scale, s, ease),
        hero: () => rig.hero(),
        heroCtl: () => rig.heroCtl,
        cameraYaw: () => rig.control().cameraYaw,
        respawnAt: () => spawn,
        terrainAt: (x, z) => rig.terrainAt(x, z),
        encounter: o.encounter === false ? null : "world",
        // a ragdoll goes with its chunk when that unloads (phase D joined: a warp away no longer leaves one on no ground)
        chunkOf: (x, z) => world.chunkAt(x, z)?.id ?? null,
        // the rpg layer's skill-tree upgrades swap the hero's moves; the quick switch cycles the weapons it owns
        moveFor: (e, m) => r.moveFor(e, m),
        ownedWeapons: () => r.ownedWeapons(),
        director: true,
        difficulty: o.difficulty ?? "normal",
        // the breakables are combat targets (team "prop"): they stand where they were placed
        otherPose: (id) => {
          if (!things || id < BREAKABLE_IDS) return null;
          const p = thingsContent.placements.filter((x) => x.kind === "breakable")[id - BREAKABLE_IDS];
          return p ? { feet: things.at(p.id), yaw: p.yaw } : null;
        },
      });
      fight = f;
      let others: OtherInteractables[] = [r.others];
      const t = createThings({ collision: rig.collision, actions, rng, content: thingsContent, fight: () => f, nav: n, hero: () => rig.hero(), groundAt: (x, z) => world.heightAt(x, z), chunkOf: (id) => chunkOfThing.get(id)!, bag: { inventory: r.inventory, state: () => r.state().bag }, others: () => others });
      things = t;
      // phase D: the addons (the story layer…) on the same fight, things, sky and navmesh
      const addons = (o.addons ?? []).map((a) => a({ world, rig, rng, fight: () => f, things: () => t, thingsContent, sky: () => need(sky, "the sky"), nav: () => n }));
      others = [r.others, ...addons.flatMap((a) => (a.interactables ? [a.interactables] : []))];
      const s = createWorldSim({
        world,
        collision: rig.collision,
        nav: n,
        navSource,
        slicer: chunkSlicer,
        busyElsewhere: () => t.slicer.busy(),
        tileGone: (tile) => void t.slicer.drop(tile),
        streaming: wc.streaming,
        hero: () => {
          const h = rig.hero();
          return { x: h.feet.x, z: h.feet.z, vx: h.vel.x, vz: h.vel.z };
        },
        // the live enemies, and the camps until the fight has placed them on its first step
        pins: () => {
          const st = f.state();
          const out: { x: number; z: number }[] = [];
          for (const e of st.enemies) if (e.alive) out.push({ x: e.pos[0], z: e.pos[2] });
          if (!st.placed) out.push(...camps);
          for (const a of addons) if (a.pins) out.push(...a.pins());
          return out;
        },
        characters: () => {
          const h = rig.hero();
          const out = [{ label: "hero", x: h.feet.x, z: h.feet.z }];
          for (const e of f.state().enemies) if (e.alive) out.push({ label: `${e.kind} ${e.id}`, x: e.pos[0], z: e.pos[2] });
          return out;
        },
      });
      sim = s;
      const sk = createSky({ content: skyContent, rng });
      sky = sk;
      const roomsModule = createInteriorsModule({ rig, interiors: rooms.map((r) => r.spec), doors: [], nums: kit.content.look.interiors });
      places = roomsModule.sim;
      const more = o.extra?.({ rig, mount: () => self });
      return {
        // the world's module first: a restore rebuilds its nav tiles before the fight's crowd comes back on them; the
        // sky's system runs first in the step (its order), the interiors after the physics step
        modules: [s.module, sk.module, roomsModule.module, ...f.modules, ...t.modules, ...r.modules, ...(more?.modules ?? []), ...addons.flatMap((a) => a.modules)],
        build: (w) => {
          sk.build(w);
          roomsModule.build(w);
          const h = rig.hero();
          s.prime({ x: h.feet.x, z: h.feet.z }, camps);
          // one physics step builds the broad phase over the primed chunks, so rays see them from the first sim step
          rig.collision.step();
          f.build(w);
          t.build(w);
          r.build(w);
          more?.build(w);
          for (const a of addons) a.build?.(w);
        },
        hashed: [...sk.hashed, ...roomsModule.hashed, ...f.hashed, ...t.hashed, ...r.hashed, ...(more?.hashed ?? []), ...addons.flatMap((a) => a.hashed ?? [])],
        // a door's fade holds the hero first (none in the valley today: every room is seamless), then a gather's pose,
        // then the fight's
        heroDrive: () => roomsModule.heroDrive() ?? t.heroDrive(),
        claimsInteract: () => t.claimsInteract(),
        heroScale: () => r.heroScale(),
      };
    },
  };
  const need = <T>(v: T | null, what: string): T => {
    if (!v) throw new Error(`world: ${what} not set up yet`);
    return v;
  };
  const self: WorldMount = {
    variant,
    content: wc,
    world,
    kit,
    owners,
    rooms,
    water,
    fight: () => need(fight, "the fight"),
    sky: () => need(sky, "the sky"),
    places: () => need(places, "the interiors"),
    thingsContent,
    things: () => need(things, "the things"),
    sim: () => need(sim, "the sim tier"),
    nav: () => need(nav, "the navmesh"),
    rig: () => need(rigRef, "the rig"),
    rpgContent,
    conditions,
    stations,
    rpg: () => need(rpg, "the rpg layer"),
  };
  return self;
}
